<template>
  <li :class="nodeClasses">
    <div class="flex items-center space-x-2 pl-4" :class="headNodeClasses">
      <span v-if="number" class="text-gray-500 text-sm shrink-0 mr-2">{{ number }}.</span>
      <span v-if="node.file">
        <FileIcon/>
      </span>
      <span v-else-if="node.product">
        <ProductIcon/>
      </span>
      <span v-else-if="node.technology">
          <TechnologyIcon/>
        </span>
      <span v-else>
        <OperationIcon/>
      </span>
      <span v-if="type === 'operation'" class="w-full">
        <button
          @click="viewDetailOperation(node.id, node.technology_id)"
          @mouseenter="hoverTechnology(node.technology_id)"
          @mouseleave="hoverTechnology(null)"
          :class="[
            'text-left w-full px-2 py-1 rounded-md transition-all duration-200',
            'hover:bg-blue-50 hover:text-blue-700 hover:ring-1 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
            isActiveOperation ? 'text-blue-700 bg-blue-50 ring-1 ring-blue-200' : 'text-gray-800'
          ]"
        >
          {{ node.name }}
        </button>
      </span>
      <span v-if="node.product" class="w-full">
        <button
          @click="viewDetailMaterial(node.product?.id)"
          :class="[
            'text-left w-full px-2 py-1 rounded-md transition-all duration-200',
            'hover:bg-blue-50 hover:text-blue-700 hover:ring-1 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
            isActiveProduct ? 'text-blue-700 bg-blue-50 ring-1 ring-blue-200' : 'text-gray-800'
          ]"
        >
          {{ node.product?.sku ? node.product.sku + ' - ' + node.product.name : node.product?.name }}
        </button>
      </span>
        <span v-if="node.technology" class="w-full">
        <button
          @click="viewDetailTechnology(node.technology?.name)"
          :class="[
            'text-left w-full px-2 py-1 rounded-md transition-all duration-200 whitespace-normal break-words',
            'hover:bg-blue-50 hover:text-blue-700 hover:ring-1 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
            isActiveTechnology ? 'text-blue-700 bg-blue-50 ring-1 ring-blue-200' : 'text-gray-800'
          ]"
        >
          {{ node.technology?.name }}
        </button>
      </span>
      <span v-if="node.file" class="w-full">
        <button
          @click="viewDetailFile(node.file)"
          :class="[
            'text-left w-full px-2 py-1 rounded-md transition-all duration-200',
            'hover:bg-blue-50 hover:text-blue-700 hover:ring-1 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
            'text-gray-800'
          ]"
        >
          {{ node.file?.original_name || node.file?.name || 'Документ' }}
        </button>
      </span>
    </div>

    <ul v-if="node.technology?.operations?.length" class="mt-2 pl-4">
      <tree-node
        v-for="op in node.technology.operations"
        :key="op.id"
        :node="op"
        type="operation"
        :activeProductId="activeProductId"
        :activeTechnologyName="activeTechnologyName"
        :activeOperationId="activeOperationId"
        :activeTechnologyId="activeTechnologyId"
        :hoveredTechnologyId="hoveredTechnologyId"
        @viewDetailOperation="viewDetailOperation"
        @viewDetailStep="bubbleViewDetailStep"
        @hoverTechnology="hoverTechnology"
      >
        <template v-if="op.steps" #default>
          <ul class="ml-4 border-l-2 border-gray-300 pl-4 mt-2 space-y-3">
            <li
              v-for="(step, stepIndex) in op.steps"
              :key="step.id"
              @click.stop="emitViewDetailStep(step.id, op.id, op.technology_id)"
              class="bg-gray-50 rounded-lg p-3 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 cursor-pointer"
            >
              <div class="font-semibold text-base text-gray-800 mb-1">
                {{ stepIndex + 1 }}. {{ step.name }}<span class="font-normal text-gray-400 text-xs">{{ stepTypeSuffix(step.type) }}</span>
              </div>
              <div v-if="step.description" class="italic text-sm text-gray-600 mb-2">
                {{ step.description }}
              </div>

              <!-- Отображение продуктов из шага -->
              <ol v-if="step.products && step.products.length > 0" class="ml-4 list-decimal text-sm text-gray-700">
                <li v-for="product in step.products" :key="`product-${product.id}`">
                  <span :class="{ 'line-through text-gray-500': product?.deleted_at }">{{ getProductDisplayName(product) }}</span>
                  <span v-if="product?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                  — <span class="font-medium">{{ formatQuantity(product.count) }}</span> {{ getUnitLabel(product) }}
                </li>
              </ol>
              
              <!-- Для обратной совместимости -->
              <ul v-else-if="step.components?.length" class="ml-4 list-disc text-sm text-gray-700">
                <li v-for="comp in step.components" :key="comp.id">
                  <span :class="{ 'line-through text-gray-500': comp.product?.deleted_at }">{{ comp.product?.sku ? comp.product.sku + ' - ' + comp.product.name : comp.product?.name }}</span>
                  <span v-if="comp.product?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                  — <span class="font-medium">{{ formatQuantity(comp.count) }}</span> {{ getUnitLabel(comp.product) }}
                </li>
              </ul>
            </li>
          </ul>
        </template>
      </tree-node>
    </ul>
    <slot />
  </li>
</template>

<script>
import ProductIcon from "@/components/production/ProductIcon.vue";
import TechnologyIcon from "@/components/production/TechnologyIcon.vue";
import OperationIcon from "@/components/production/OperationIcon.vue";
import FileIcon from "@/components/production/FileIcon.vue";

export default {
  name: 'TreeNode',
  components: {OperationIcon, TechnologyIcon, ProductIcon, FileIcon},
  computed: {
    // Проверяем, активен ли текущий продукт
    isActiveProduct() {
      return this.node.product && this.activeProductId && this.node.product.id == this.activeProductId;
    },
    // Проверяем, активна ли текущая технология
    isActiveTechnology() {
      return this.node.technology && this.activeTechnologyName && this.node.technology.name === this.activeTechnologyName;
    },
    // Проверяем, активна ли текущая операция
    isActiveOperation() {
      return this.type === 'operation' && this.activeOperationId && this.node.id == this.activeOperationId;
    },
    // Проверяем, принадлежит ли операция активной технологии
    isOperationInActiveTechnology() {
      return this.type === 'operation' && this.activeTechnologyId && this.node.technology_id == this.activeTechnologyId;
    },
    // Подсветка левой полоски технологии при наведении на операцию
    isHighlightedTechnology() {
      if (!this.node.technology) return false;
      const techId = this.node.technology_id || this.node.technology.id;
      if (!techId) return false;
      return (this.hoveredTechnologyId && String(this.hoveredTechnologyId) == String(techId)) || this.isActiveTechnology;
    },
    // Общий класс для подсветки
    nodeClasses() {
      const base = ['mb-2', 'transition-colors', 'duration-200'];
      if (this.node.technology) {
        base.push('border-l-4');
        base.push(this.isHighlightedTechnology ? 'border-blue-500' : 'border-gray-200');
      } else {
        base.push('border-l-2', 'border-gray-200');
      }
      return base;
    },
    headNodeClasses() {
      return {
        'bg-blue-50': this.isActiveProduct || this.isActiveTechnology || this.isActiveOperation,
      };
    }
  },
  mounted() {
    console.log(this.node);
  },
  methods: {
    stepTypeSuffix(type) {
      const map = {
        print: ' (печать)',
        packaging: ' (упаков.)',
        picking: ' (компл.)',
        software: ' (Используемое ПО)',
      };
      return map[type] || '';
    },
    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    getUnitLabel(product) {
      return product?.unit_short_name || product?.unit?.short_name || 'шт';
    },
    viewDetailMaterial(id) {
      this.$emit('viewDetailMaterial', id);
    },
    viewDetailTechnology(name) {
      this.$emit('viewDetailTechnology', name);
    },
    viewDetailOperation(id, technologyId) {
      this.$emit('viewDetailOperation', id, technologyId);
    },
    emitViewDetailStep(stepId, operationId, technologyId) {
      this.$emit('viewDetailStep', { stepId, operationId, technologyId });
    },
    bubbleViewDetailStep(payload) {
      this.$emit('viewDetailStep', payload);
    },
    viewDetailFile(file) {
      this.$emit('viewDetailFile', file);
    },
    hoverTechnology(technologyId) {
      this.$emit('hoverTechnology', technologyId);
    },
    getProductDisplayName(product) {
      const hasSku = product.sku && product.sku.trim() !== '';
      const hasName = product.name && product.name.trim() !== '';
      
      if (hasSku && hasName) {
        return `${product.sku} - ${product.name}`;
      } else if (hasSku) {
        return product.sku;
      } else if (hasName) {
        return product.name;
      } else {
        return 'Без названия';
      }
    }
  },
  props: ['node', 'type', 'number', 'activeProductId', 'activeTechnologyName', 'activeOperationId', 'activeTechnologyId', 'hoveredTechnologyId']
};
</script>
